import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  AppBar,
  Toolbar,
  IconButton,
  Paper,
  Divider,
} from "@mui/material";
import {
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as WaitlistIcon,
  PersonAdd as RegisterIcon,
  PersonRemove as UnregisterIcon,
  Group as GroupIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  AccessTime as ClockIcon,
  Sports as SportsIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { LeagueDay, Player } from "../types";
import CountdownTimer from "../components/CountdownTimer";
import VenueBadge from "../components/VenueBadge";
import { API_BASE_URL } from "../config/api";
import { formatLeagueDayDate, formatLeagueDayDateCustom } from "../utils/dateFormat";

interface OpenLeagueDay extends LeagueDay {
  isRegistered: boolean;
  registrationStatus?: "confirmed" | "waitlist";
  waitlistPosition?: number;
}

function PlayerLeagueDays() {
  const { playerToken, playerUser } = useAuth();
  const navigate = useNavigate();
  const [leagueDays, setLeagueDays] = useState<OpenLeagueDay[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paymentError, setPaymentError] = useState<{ message: string; unpaidLeagueDays: number[]; totalOwed: number } | null>(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [registrationListDialogOpen, setRegistrationListDialogOpen] =
    useState(false);
  const [selectedLeagueDay, setSelectedLeagueDay] =
    useState<OpenLeagueDay | null>(null);
  const [penaltyDialogOpen, setPenaltyDialogOpen] = useState(false);
  const [penaltyInfo, setPenaltyInfo] = useState<{
    points: number;
    message: string;
    leagueDayId: string;
    leagueDayDate: Date;
  } | null>(null);
  const [, setTimerUpdate] = useState(0);
  const [playerMatchesDialogOpen, setPlayerMatchesDialogOpen] = useState(false);
  const [selectedLeagueDayForMatches, setSelectedLeagueDayForMatches] =
    useState<OpenLeagueDay | null>(null);
  const [isGuest, setIsGuest] = useState(false);
  const [isInactive, setIsInactive] = useState(false);

  // Check if player is a guest or inactive and redirect if they are
  useEffect(() => {
    const checkPlayerStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/player/me`, {
          headers: {
            Authorization: `Bearer ${playerToken}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.isGuest) {
            setIsGuest(true);
            navigate("/player/dashboard");
          }
          if (data.isTournamentOnly) {
            navigate("/player/dashboard");
          }
          if (data.isActive === false) {
            setIsInactive(true);
            navigate("/player/dashboard");
          }
        }
      } catch (error) {
        console.error("Error checking player status:", error);
      }
    };

    checkPlayerStatus();
  }, [playerToken, navigate]);

  useEffect(() => {
    if (!isGuest && !isInactive) {
      fetchOpenLeagueDays();
      fetchPlayers();
    }
    // Force re-render every second for timer
    const interval = setInterval(() => {
      setTimerUpdate((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [isGuest]);

  const fetchPlayers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/player/players`, {
        headers: {
          Authorization: `Bearer ${playerToken}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setPlayers(data);
      }
    } catch (error) {
      console.error("Error fetching players:", error);
    }
  };

  const fetchOpenLeagueDays = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/player/league-days/open`,
        {
          headers: {
            Authorization: `Bearer ${playerToken}`,
          },
        },
      );
      if (response.ok) {
        const data = await response.json();
        setLeagueDays(data);
        setError("");
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to fetch league days");
      }
    } catch (error) {
      console.error("Error fetching league days:", error);
      setError("Failed to fetch league days");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (leagueDayId: string) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/player/league-days/${leagueDayId}/register`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${playerToken}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setPaymentError(null);
        setSuccessMessage(
          data.status === "confirmed"
            ? "Successfully registered for this league day!"
            : "Added to waitlist. You'll be notified if a spot opens up.",
        );
        fetchOpenLeagueDays();
      } else {
        const errorData = await response.json();
        if (errorData.error === "OUTSTANDING_BALANCE") {
          setPaymentError({ message: errorData.message, unpaidLeagueDays: errorData.unpaidLeagueDays, totalOwed: errorData.totalOwed });
        } else {
          setError(errorData.error || "Failed to register");
        }
      }
    } catch (error) {
      console.error("Error registering:", error);
      setError("Failed to register");
    }
  };

  const handleUnregister = async (leagueDayId: string) => {
    // Find the league day to get its date
    const leagueDay = leagueDays.find((ld) => ld.id === leagueDayId);
    if (!leagueDay) {
      setError("League day not found");
      return;
    }

    // Calculate hours until league day
    const now = new Date();
    const leagueDayDate = new Date(leagueDay.date);
    const hoursUntilMatch =
      (leagueDayDate.getTime() - now.getTime()) / (1000 * 60 * 60);

    let penaltyPoints = 0;
    let message = "";

    if (hoursUntilMatch > 72) {
      // More than 72 hours: No penalty
      penaltyPoints = 0;
      message =
        "No penalty will be applied since you're cancelling more than 72 hours in advance. However, you must inform Gonçalo about the cancellation so a substitute can be found.";
    } else if (hoursUntilMatch > 24) {
      // Between 72 and 24 hours: 3 points penalty
      penaltyPoints = 3;
      message =
        "You will lose 3 points in the general ranking as you're cancelling between 72 and 24 hours before the match. You must also inform Gonçalo about the cancellation so a substitute can be found.";
    } else {
      // Less than 24 hours: 6 points penalty
      penaltyPoints = 6;
      message =
        "You will lose 6 points in the general ranking as you're cancelling less than 24 hours before the match. You must also inform Gonçalo about the cancellation so a substitute can be found.";
    }

    // Show penalty dialog
    setPenaltyInfo({
      points: penaltyPoints,
      message,
      leagueDayId,
      leagueDayDate,
    });
    setPenaltyDialogOpen(true);
  };

  const calculatePenaltyPoints = (leagueDayDate: Date): number => {
    const now = new Date();
    const hoursUntilMatch =
      (leagueDayDate.getTime() - now.getTime()) / (1000 * 60 * 60);

    if (hoursUntilMatch > 72) {
      return 0;
    } else if (hoursUntilMatch > 24) {
      return 3;
    } else {
      return 6;
    }
  };

  const handleViewMatches = (leagueDay: OpenLeagueDay) => {
    setSelectedLeagueDayForMatches(leagueDay);
    setPlayerMatchesDialogOpen(true);
  };

  const getPlayerMatches = (leagueDay: OpenLeagueDay) => {
    if (!leagueDay.rounds || !playerUser?.playerId) return [];

    const playerMatches: any[] = [];

    leagueDay.rounds.forEach((round) => {
      round.matches.forEach((match) => {
        const isInMatch =
          match.pair1.player1Id === playerUser.playerId ||
          match.pair1.player2Id === playerUser.playerId ||
          match.pair2.player1Id === playerUser.playerId ||
          match.pair2.player2Id === playerUser.playerId;

        if (isInMatch) {
          playerMatches.push({
            roundNumber: round.roundNumber,
            courtNumber: match.courtNumber,
            match,
          });
        }
      });
    });

    return playerMatches;
  };

  const getPlayerName = (playerId: string) => {
    const player = players.find((p) => p.id === playerId);
    return player?.displayName || player?.name || "Unknown Player";
  };

  const confirmUnregister = async () => {
    if (!penaltyInfo) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/player/league-days/${penaltyInfo.leagueDayId}/unregister`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${playerToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            penaltyPoints: penaltyInfo.points,
            cancelledAt: new Date().toISOString(),
            leagueDayDate: penaltyInfo.leagueDayDate.toISOString(),
          }),
        },
      );

      if (response.ok) {
        setSuccessMessage("Successfully unregistered from this league day.");
        setPenaltyDialogOpen(false);
        setPenaltyInfo(null);
        fetchOpenLeagueDays();
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to unregister");
        setPenaltyDialogOpen(false);
      }
    } catch (error) {
      console.error("Error unregistering:", error);
      setError("Failed to unregister");
      setPenaltyDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "background.default" }}>
      {/* Header */}
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate("/player/dashboard")}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            League Days Registration
          </Typography>
          <IconButton
            color="inherit"
            onClick={() => navigate("/player/profile")}
            sx={{
              mr: { xs: 0.5, sm: 1 },
              display: { xs: "inline-flex", sm: "none" },
            }}
          >
            <PersonIcon />
          </IconButton>
          <Button
            color="inherit"
            startIcon={<PersonIcon />}
            onClick={() => navigate("/player/profile")}
            sx={{
              mr: 1,
              display: { xs: "none", sm: "inline-flex" },
            }}
          >
            Profile
          </Button>
          <IconButton
            color="inherit"
            onClick={() => navigate("/player/dashboard")}
            sx={{
              display: { xs: "inline-flex", sm: "none" },
            }}
          >
            <HomeIcon />
          </IconButton>
          <Button
            color="inherit"
            startIcon={<HomeIcon />}
            onClick={() => navigate("/player/dashboard")}
            sx={{
              display: { xs: "none", sm: "inline-flex" },
            }}
          >
            Home
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper
          elevation={0}
          sx={{ p: 3, mb: 3, backgroundColor: "background.paper" }}
        >
          <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold" }}>
            📅 Available League Days
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Register for upcoming league days or view your current registrations
          </Typography>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {paymentError && (
          <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setPaymentError(null)}>
            {paymentError.message}
          </Alert>
        )}

        {successMessage && (
          <Alert
            severity="success"
            sx={{ mb: 3 }}
            onClose={() => setSuccessMessage("")}
          >
            {successMessage}
          </Alert>
        )}

        {/* Prominent Next Registration Timer */}
        {(() => {
          const nextScheduled = leagueDays
            .filter(
              (ld) =>
                !ld.registrationOpen &&
                ld.registrationScheduledFor &&
                new Date(ld.registrationScheduledFor) > new Date(),
            )
            .sort(
              (a, b) =>
                new Date(a.registrationScheduledFor!).getTime() -
                new Date(b.registrationScheduledFor!).getTime(),
            )[0];

          if (nextScheduled) {
            return (
              <Card
                elevation={3}
                sx={{
                  mb: 3,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", textAlign: "center" }}
                    >
                      🔔 Upcoming Registration
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ textAlign: "center", opacity: 0.9 }}
                    >
                      League Day on{" "}
                      {formatLeagueDayDateCustom(
                        nextScheduled.date,
                        {
                          weekday: "long",
                          month: "long",
                          day: "numeric",
                        },
                        "en-US"
                      )}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        textAlign: "center",
                        fontWeight: "bold",
                        opacity: 0.95,
                      }}
                    >
                      Opens on:{" "}
                      {new Date(
                        nextScheduled.registrationScheduledFor!,
                      ).toLocaleString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </Typography>
                    <Box sx={{ width: "100%", maxWidth: "600px" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 2,
                          backgroundColor: "rgba(255, 255, 255, 0.2)",
                          borderRadius: 1,
                          justifyContent: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <ClockIcon sx={{ color: "white" }} />
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: "medium", color: "white" }}
                        >
                          Registration opens in:
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            fontFamily: "monospace",
                          }}
                        >
                          {(() => {
                            const difference =
                              new Date(
                                nextScheduled.registrationScheduledFor!,
                              ).getTime() - new Date().getTime();
                            const days = Math.floor(
                              difference / (1000 * 60 * 60 * 24),
                            );
                            const hours = Math.floor(
                              (difference / (1000 * 60 * 60)) % 24,
                            );
                            const minutes = Math.floor(
                              (difference / 1000 / 60) % 60,
                            );
                            const seconds = Math.floor(
                              (difference / 1000) % 60,
                            );
                            return (
                              <>
                                {days > 0 && (
                                  <Typography
                                    variant="h6"
                                    sx={{ fontWeight: "bold", color: "white" }}
                                  >
                                    {days}d
                                  </Typography>
                                )}
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: "bold", color: "white" }}
                                >
                                  {hours}h
                                </Typography>
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: "bold", color: "white" }}
                                >
                                  {minutes}m
                                </Typography>
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: "bold", color: "white" }}
                                >
                                  {seconds}s
                                </Typography>
                              </>
                            );
                          })()}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          }
          return null;
        })()}

        {leagueDays.length === 0 ? (
          <Paper elevation={0} sx={{ p: 4, textAlign: "center" }}>
            <CalendarIcon
              sx={{ fontSize: 60, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No upcoming league days found.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Check back later for new league days to register.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {leagueDays
              .sort(
                (a, b) =>
                  new Date(a.date).getTime() - new Date(b.date).getTime(),
              )
              .map((leagueDay) => (
                <Card
                  key={leagueDay.id}
                  elevation={2}
                  sx={{
                    transition: "transform 0.2s",
                    "&:hover": { transform: "translateY(-4px)" },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      mb={2}
                      flexWrap="wrap"
                      gap={2}
                    >
                      <Box>
                        {leagueDay.name && (
                          <Typography
                            variant="h6" 
                            sx={{
                              fontWeight: "bold",
                              mb: 1,
                              color: "primary.main"
                            }}
                          >
                            {leagueDay.name}
                          </Typography>
                        )}
                        <Box display="flex" alignItems="center" mb={1}>
                          <CalendarIcon sx={{ mr: 1, color: "primary.main" }} />
                          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                            {formatLeagueDayDate(leagueDay.date)}
                          </Typography>
                        </Box>
                        {leagueDay.time && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 4 }}
                          >
                            ⏰ Time: {leagueDay.time}
                          </Typography>
                        )}
                        {leagueDay.venueName && (
                          <Box sx={{ ml: 4, mt: 0.5 }}>
                            <VenueBadge
                              venueName={leagueDay.venueName}
                              venueAddress={leagueDay.venueAddress}
                              variant="body2"
                            />
                          </Box>
                        )}
                        <Box sx={{ ml: 4, mt: 0.5 }}>
                          <Chip
                            label={leagueDay.division}
                            size="small"
                            color={leagueDay.division !== playerUser?.division ? "warning" : "default"}
                            variant={leagueDay.division !== playerUser?.division ? "filled" : "outlined"}
                            icon={<span style={{ fontSize: 14, marginLeft: 6 }}>🏆</span>}
                          />
                          {leagueDay.division !== playerUser?.division && (
                            <Typography variant="caption" color="warning.main" sx={{ ml: 1 }}>
                              Guest division
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {leagueDay.isRegistered && (
                        <Chip
                          label={
                            leagueDay.registrationStatus === "confirmed"
                              ? "Confirmed"
                              : `Waitlist #${leagueDay.waitlistPosition}`
                          }
                          color={
                            leagueDay.registrationStatus === "confirmed"
                              ? "success"
                              : "warning"
                          }
                          icon={
                            leagueDay.registrationStatus === "confirmed" ? (
                              <CheckCircleIcon />
                            ) : (
                              <WaitlistIcon />
                            )
                          }
                        />
                      )}
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box mb={2}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={2}
                        flexWrap="wrap"
                      >
                        <Chip
                          icon={<GroupIcon />}
                          label={`${leagueDay.selectedPlayers?.length || 0}/${leagueDay.playerLimit || 12} Registered`}
                          color="primary"
                          variant="outlined"
                        />
                        {leagueDay.waitlistPlayerIds &&
                          leagueDay.waitlistPlayerIds.length > 0 && (
                            <Chip
                              icon={<WaitlistIcon />}
                              label={`${leagueDay.waitlistPlayerIds.length} on Waitlist`}
                              color="warning"
                              variant="outlined"
                            />
                          )}
                      </Box>
                    </Box>

                    {leagueDay.isRegistered && !leagueDay.isDraft ? (
                      <>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<SportsIcon />}
                          onClick={() => handleViewMatches(leagueDay)}
                          fullWidth
                        >
                          Show My Matches
                        </Button>
                        <Alert severity="error" sx={{ mt: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "bold" }}
                          >
                            In order to unregister yourself from this league
                            day you have to contact one of the admins to do
                            so.
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Beware cancelling now will put a penalty of{" "}
                            <strong>
                              {calculatePenaltyPoints(
                                new Date(leagueDay.date),
                              )}{" "}
                              points
                            </strong>{" "}
                            on your profile.
                          </Typography>
                        </Alert>
                      </>
                    ) : !leagueDay.registrationOpen &&
                    leagueDay.registrationScheduledFor ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            textAlign: "center",
                            fontWeight: "bold",
                            color: "primary.main",
                          }}
                        >
                          Opens on:{" "}
                          {new Date(
                            leagueDay.registrationScheduledFor,
                          ).toLocaleString("en-US", {
                            weekday: "short",
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </Typography>
                        <CountdownTimer
                          targetDate={
                            new Date(leagueDay.registrationScheduledFor)
                          }
                          onComplete={fetchOpenLeagueDays}
                          prefix="Registration opens in:"
                        />
                      </Box>
                    ) : !leagueDay.registrationOpen ? (
                      <Button variant="outlined" disabled fullWidth>
                        Registration opens soon - Date will be announced
                      </Button>
                    ) : leagueDay.isRegistered ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <>
                          <Box
                            sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                          >
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<UnregisterIcon />}
                              onClick={() => handleUnregister(leagueDay.id)}
                              sx={{ flex: 1, minWidth: "150px" }}
                            >
                              Unregister
                            </Button>
                            {leagueDay.registrationOpen &&
                              (leagueDay.selectedPlayers?.length > 0 ||
                                (leagueDay.waitlistPlayerIds &&
                                  leagueDay.waitlistPlayerIds.length >
                                    0)) && (
                                <Button
                                  variant="outlined"
                                  startIcon={<GroupIcon />}
                                  onClick={() => {
                                    setSelectedLeagueDay(leagueDay);
                                    setRegistrationListDialogOpen(true);
                                  }}
                                  sx={{ flex: 1, minWidth: "150px" }}
                                >
                                  View List
                                </Button>
                              )}
                          </Box>
                        </>
                      </Box>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<RegisterIcon />}
                        onClick={() => handleRegister(leagueDay.id)}
                        fullWidth
                      >
                        {(leagueDay.selectedPlayers?.length || 0) >=
                        (leagueDay.playerLimit || 12)
                          ? "Join Waitlist"
                          : "Register"}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
          </Box>
        )}
      </Container>

      {/* Registration List Dialog */}
      <Dialog
        open={registrationListDialogOpen}
        onClose={() => setRegistrationListDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <GroupIcon sx={{ mr: 1, verticalAlign: "middle" }} />
          Registration List
        </DialogTitle>
        <DialogContent>
          {selectedLeagueDay && (
            <>
              {/* Confirmed Players */}
              <Box mb={3}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "success.main" }}
                >
                  Confirmed Players (
                  {selectedLeagueDay.selectedPlayers?.length || 0}/
                  {selectedLeagueDay.playerLimit || 12})
                </Typography>
                {selectedLeagueDay.selectedPlayers &&
                selectedLeagueDay.selectedPlayers.length > 0 ? (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {selectedLeagueDay.selectedPlayers.map(
                      (playerId, index) => {
                        const player = players.find((p) => p.id === playerId);
                        return (
                          <Box
                            key={playerId}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              p: 1,
                              backgroundColor: "success.light",
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: "bold",
                                mr: 1,
                                minWidth: "30px",
                              }}
                            >
                              #{index + 1}
                            </Typography>
                            <Typography variant="body2">
                              {player?.name || "Unknown Player"}
                            </Typography>
                          </Box>
                        );
                      },
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No confirmed players yet
                  </Typography>
                )}
              </Box>

              {/* Waitlist */}
              {selectedLeagueDay.waitlistPlayerIds &&
                selectedLeagueDay.waitlistPlayerIds.length > 0 && (
                  <Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ color: "warning.main" }}
                    >
                      Waitlist ({selectedLeagueDay.waitlistPlayerIds.length})
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {selectedLeagueDay.waitlistPlayerIds.map(
                        (playerId, index) => {
                          const player = players.find((p) => p.id === playerId);
                          return (
                            <Box
                              key={playerId}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                p: 1,
                                backgroundColor: "warning.light",
                                borderRadius: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: "bold",
                                  mr: 1,
                                  minWidth: "30px",
                                }}
                              >
                                #{index + 1}
                              </Typography>
                              <Typography variant="body2">
                                {player?.name || "Unknown Player"}
                              </Typography>
                            </Box>
                          );
                        },
                      )}
                    </Box>
                  </Box>
                )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegistrationListDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Penalty Confirmation Dialog */}
      <Dialog
        open={penaltyDialogOpen}
        onClose={() => setPenaltyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {penaltyInfo?.points === 0
            ? "Confirm Cancellation"
            : "Cancellation Penalty"}
        </DialogTitle>
        <DialogContent>
          {penaltyInfo && (
            <Box sx={{ mt: 1 }}>
              <Alert
                severity={
                  penaltyInfo.points === 0
                    ? "info"
                    : penaltyInfo.points === 3
                      ? "warning"
                      : "error"
                }
                sx={{ mb: 2 }}
              >
                {penaltyInfo.points > 0 && (
                  <Typography variant="h6" gutterBottom>
                    -{penaltyInfo.points} Points Penalty
                  </Typography>
                )}
                <Typography variant="body2">{penaltyInfo.message}</Typography>
              </Alert>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                League Day Date:{" "}
                {formatLeagueDayDateCustom(
                  penaltyInfo.leagueDayDate,
                  {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  },
                  "en-GB"
                )}
              </Typography>

              <Typography
                variant="body2"
                color="error"
                fontWeight="bold"
                sx={{
                  mt: 2,
                  p: 1.5,
                  backgroundColor: "error.light",
                  borderRadius: 1,
                  border: "2px solid",
                  borderColor: "error.main",
                }}
              >
                ⚠️ It is necessary to inform Gonçalo of this cancellation to
                avoid confusion on the league day.
              </Typography>

              {penaltyInfo.points > 0 && (
                <Typography
                  variant="body2"
                  color="error"
                  fontWeight="bold"
                  sx={{ mt: 2 }}
                >
                  By confirming, you acknowledge that {penaltyInfo.points}{" "}
                  points will be deducted from your ranking and this penalty
                  will be recorded.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPenaltyDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={confirmUnregister}
            variant="contained"
            color={penaltyInfo?.points === 0 ? "primary" : "error"}
          >
            Confirm Unregistration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Player Matches Dialog */}
      <Dialog
        open={playerMatchesDialogOpen}
        onClose={() => setPlayerMatchesDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SportsIcon />
            My Matches
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedLeagueDayForMatches && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {formatLeagueDayDate(selectedLeagueDayForMatches.date)}
              </Typography>

              {getPlayerMatches(selectedLeagueDayForMatches).length > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    mt: 2,
                  }}
                >
                  {getPlayerMatches(selectedLeagueDayForMatches).map(
                    ({ roundNumber, courtNumber, match }) => {
                      const isPlayer1Side =
                        match.pair1.player1Id === playerUser?.playerId ||
                        match.pair1.player2Id === playerUser?.playerId;

                      const partnerName = isPlayer1Side
                        ? match.pair1.player1Id === playerUser?.playerId
                          ? getPlayerName(match.pair1.player2Id)
                          : getPlayerName(match.pair1.player1Id)
                        : match.pair2.player1Id === playerUser?.playerId
                          ? getPlayerName(match.pair2.player2Id)
                          : getPlayerName(match.pair2.player1Id);

                      const opponent1Name = isPlayer1Side
                        ? getPlayerName(match.pair2.player1Id)
                        : getPlayerName(match.pair1.player1Id);
                      const opponent2Name = isPlayer1Side
                        ? getPlayerName(match.pair2.player2Id)
                        : getPlayerName(match.pair1.player2Id);

                      return (
                        <Card
                          key={`${roundNumber}-${courtNumber}`}
                          elevation={2}
                        >
                          <CardContent>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 2,
                              }}
                            >
                              <Chip
                                label={`Round ${roundNumber}`}
                                color="primary"
                                size="small"
                              />
                              <Chip
                                label={`Court ${courtNumber}`}
                                variant="outlined"
                                size="small"
                              />
                            </Box>
                            <Box sx={{ textAlign: "center" }}>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: "bold",
                                  color: "primary.main",
                                }}
                              >
                                You & {partnerName}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ my: 1 }}
                              >
                                vs
                              </Typography>
                              <Typography variant="body1">
                                {opponent1Name} & {opponent2Name}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    },
                  )}
                </Box>
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No matches found. Matchups may not have been generated yet.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlayerMatchesDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PlayerLeagueDays;
